import { useAppPermissions } from '../../../hooks/useAppPermissions';
import { isAdminUser } from '../adminlayout/sidebarFilters';

/**
 * 마케터 탭 관련 자격 판정. UserManagement.jsx 가 300줄에 닿아 분리했다(2026-09-09).
 *
 * 2026-10-21 사용자: "마케팅명부와 직원관리가 합쳐졌으면 기능이나 권한도 합쳐져야해 직원관리를 부여받으면
 *   마케팅명부도 수정이 가능해야지" / "직원관리를 부여받으면 그냥 권한과 직결이야".
 *   → '마케팅명부 관리'(SALES_ROSTER_MANAGEMENT) 열쇠를 없애고 그 회사 직원관리 열쇠가 마케터의 모든 쓰기를 한다.
 *   서버 판정(marketer-write-scope.service · marketer-company-links.route codesFor)과 같은 규칙이어야 한다
 *   — 어긋나면 "버튼은 보이는데 저장은 403".
 *
 *   canManageMarketers        — 운영자: 전 회사 마케터.
 *   canManageCompanyMarketers — 이 회사 직원관리 담당자: 이 회사 마케터의 등록·수정·비번초기화·등급·퇴사.
 *                               EXTERNAL_{회사}_USER_MANAGEMENT, 또는 자기 회사 화면이면 USER_MANAGEMENT.
 *   canAssignMarketers        — 담당 회사 지정·해제(위와 같은 사람).
 *   canBulkMarketers          — 마케터 일괄등록(서버: 운영자·USER_MANAGEMENT·초기설정) — 자기 회사 화면에서만.
 *   canSeeMarketerTab         — 탭 노출. 위 + 미팅지도 관리(보기만).
 *
 * USER_MANAGEMENT 는 **자기 회사 화면일 때만** 인정한다 — ext 화면(/api/admin/ext/...)에서
 *   인정하면 본사 직원관리 담당자에게 DOT·LNJ 마케터 명단이 열린다.
 * permCodes 가 없으면 전부 false(fail-closed) — /admin/users 라우트에는 권한 가드가 없어
 *   URL 직접 입력 시 페이지 껍데기가 뜨기 때문 (2026-09-08).
 */
export function useMarketerPermissions({ apiBase, companyCode }) {
  const { permCodes } = useAppPermissions();
  const hasPerm = (code) => Boolean(permCodes && permCodes.has(code));

  const canManageMarketers = isAdminUser();
  const isOwnCompanyPage = !String(apiBase || '').startsWith('/api/admin/ext/');
  const canManageCompanyMarketers = hasPerm(`EXTERNAL_${companyCode}_USER_MANAGEMENT`)
    || (isOwnCompanyPage && hasPerm('USER_MANAGEMENT'));
  const canAssignMarketers = canManageMarketers || canManageCompanyMarketers;
  const canBulkMarketers = canManageMarketers
    || (isOwnCompanyPage && (hasPerm('USER_MANAGEMENT') || hasPerm('INITIAL_SETUP_MANAGEMENT')));

  return {
    canManageMarketers,
    canManageCompanyMarketers,
    canAssignMarketers,
    canBulkMarketers,
    canSeeMarketerTab: canAssignMarketers || hasPerm('MEETING_MAP_MANAGEMENT'),
  };
}
